<template>
  <div class="registration-form">
    <div class="form-tabs" v-if="!$props.tabsHidden">
      <a @click="activeTab=1"
         :class="{ 'active': activeTab === 1 }">
        {{ $t('registration.tabNameMobile') }}
      </a>
      <a @click="activeTab=2"
         :class="{ 'active': activeTab === 2 }">
        {{ $t('registration.tabNameCable') }}
      </a>
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 1"
           class="form-tabs--content"
           v-autofocus
           :key="'tab1'">
        <validation-observer ref="registrationObs"
                             tag="form"
                             @submit.prevent="submitMobileRegistrationForm">
          <div class="form-content">
            <div class="input-wrapper">
              <base-v-input v-model="inputNumber"
                            id="phoneNumber"
                            ref="phoneNumber"
                            container-class="phone-input"
                            name="phoneNumber"
                            :submitted="mobileRegistrationSubmitted"
                            :label="$t('registration.phoneLabel')"
                            rules="required|phoneNumberWithSpaces|max:15"
                            vid="phoneNumber"
                            :variant="inputVariant"
                            :maxlength="(13 + countSpecialChars).toString()">
                <button class="input-button"
                        :class="{'input-button-red' : changeColorOnInput}"
                        slot="input-button"
                        type="button"
                        @click="submitSmsCode"
                        :disabled="!inputNumber || buttonDisabled">
                  {{ otpCodeSent ? $t('registration.sendCodeAgain') : $t('registration.sendCode') }}
                </button>
              </base-v-input>
              <base-v-input v-model.trim="otpCode"
                            id="otpCode"
                            container-class="code-input"
                            name="otpCode"
                            rules="required|otpCode"
                            :variant="inputVariant"
                            :submitted="mobileRegistrationSubmitted"
                            :label="$t('registration.codeLabel')"
                            vid="otpCode"
                            maxlength="20"/>
            </div>
          </div>
          <div class="form-links" :class="{'align-end': $props.isConnectServiceForm}">
            <a class="back-link" :href="loginPageLink" v-if="!isConnectServiceForm">
              <img class="back-arrow" src="/assets/images/mtel_small_arrow_left.png" alt="">
              {{ $t('registration.backToRegistrationLink') }}
            </a>
            <a href="#" class="back-link" v-else @click.prevent="emitCancelEvent">
              {{ $t('common.cancel') }}
            </a>
            <base-button :disabled="!mobileFormFilled || buttonDisabled || !otpCodeSent" :loading="dataLoading" class="continue-btn" type="submit">
              {{ $t('common.continue') }}
            </base-button>
          </div>
        </validation-observer>
      </div>
      <div v-else class="form-tabs--content" :key="'tab2'" v-autofocus>
        <validation-observer ref="registrationObsFixed"
                             tag="form"
                             @submit.prevent="submitFixRegistrationForm">
          <div class="form-content">
            <div class="input-wrapper">
              <div class="label-wrapper">
                <v-popover placement="left">
                  <img class="info-icon"
                       src="/assets/images/tooltip-info-16.png"
                       alt="info-icon"
                       @click="handleTooltipClick('customerId')">
                  <template slot="popover">
                    <img class="" src="/assets/images/customer_id.png"
                         alt="info-image">
                  </template>
                </v-popover>
                <base-v-input v-model.trim="customerId"
                              id="customerId"
                              container-class="input"
                              name="customerId"
                              :variant="inputVariant"
                              rules="required|numeric|max:20"
                              :submitted="landlineRegistrationSubmitted"
                              :label="$t('registration.customerId')"
                              maxlength="20"/>
              </div>
            </div>
              <div class="label-wrapper">
                <v-popover placement="left">
                  <img class="info-icon"
                       src="/assets/images/tooltip-info-16.png"
                       alt="info-icon"
                       @click="handleTooltipClick('bill')">
                  <template slot="popover">
                    <img class="" src="/assets/images/account_image.png"
                         alt="info-image">
                  </template>
                </v-popover>
                <base-v-input v-model.trim="billNumber"
                              id="billNumber"
                              ref="billNumber"
                              container-class="input"
                              name="billNumber"
                              :variant="inputVariant"
                              :label="$t('registration.billNumberLabel')"
                              :submitted="landlineRegistrationSubmitted"
                              rules="required|numeric|max:20"
                              maxlength="20">
                </base-v-input>
              </div>
          </div>
          <div class="form-links" :class="{'align-end': $props.isConnectServiceForm}">
            <a class="back-link" :href="loginPageLink" v-if="!isConnectServiceForm">
              <img class="back-arrow" src="../../assets/images/mtel_small_arrow_left.png" alt="">
              {{ $t('registration.backToRegistrationLink') }}
            </a>
            <a href="#" class="back-link" v-else @click.prevent="emitCancelEvent">
              {{ $t('common.cancel') }}
            </a>
            <base-button :disabled="!landlineFormFilled || buttonDisabled" :loading="dataLoading" class="continue-btn" type="submit">
              {{ $t('common.continue') }}
            </base-button>
          </div>
        </validation-observer>
      </div>
    </transition>
  </div>
</template>

<script>
import { addDataLayerEvent } from '@/services/dataLayer'
import { getApiResponse } from '@/services/apiResponsesDictionary'
import { lsRemove, lsSave } from '@/utils/localStorageController'
import { customRouter } from '@/customRouter'
import errorMessageHandler from '../../mixins/errorMessageHandler'

export default {
  name: 'registration-form',
  data () {
    return {
      activeTab: 1,
      otpCode: null,
      inputNumber: '',
      billNumber: null,
      customerId: null,
      mobileRegistrationSubmitted: false,
      landlineRegistrationSubmitted: false,
      buttonDisabled: false,
      otpCodeSent: false,
      dataLoading: false,
      reconnectionData: {}
    }
  },
  props: {
    tabsHidden: Boolean,
    isConnectServiceForm: Boolean,
    connectServiceCustomerId: {
      type: String,
      default: ''
    }
  },
  mixins: [errorMessageHandler],
  computed: {
    changeColorOnInput () {
      return this.inputNumber.length > 0
    },
    mobileFormFilled () {
      return !!this.otpCode && !!this.inputNumber
    },
    landlineFormFilled () {
      return !!this.billNumber && !!this.customerId
    },
    countSpecialChars () {
      if (this.inputNumber.startsWith('00')) {
        return Math.min(this.inputNumber.replace(/[0-9a-zA-Z]/g, '').length + 2, 5)
      }
      return Math.min(this.inputNumber.replace(/[0-9a-zA-Z]/g, '').length, 5)
    },
    loginPageLink () {
      return customRouter.getRouteByName({ name: 'login' })
    },
    inputVariant () {
      return this.$props.isConnectServiceForm ? 'white-bold' : 'grey'
    }
  },
  methods: {
    async submitSmsCode () {
      this.$refs.registrationObs.reset()
      this.mobileRegistrationSubmitted = true

      const isFieldValid = await this.$refs.phoneNumber.$refs.validationProvider.validate()

      if (isFieldValid.valid) {
        if (this.$props.isConnectServiceForm) {
          this.checkReconnectionMobile()
        } else {
          this.sendOtpCodeRequest()
        }
      } else {
        this.buttonDisabled = false
      }
    },
    checkReconnectionMobile () {
      const normalizedNumber = this.inputNumber.replace(/[^0-9]/g, '')

      this.$apiRequest.get('user/service/connection/checkmobile?msisdn=' + normalizedNumber)
        .then(response => {
          this.reconnectionData = response.data
          this.sendOtpCodeRequest(response.data.customerId)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    sendOtpCodeRequest (customerId) {
      this.buttonDisabled = true

      const normalizedNumber = this.inputNumber.replace(/[^0-9]/g, '')
      let requestURL = 'register/sendotpcode'

      const dataToSend = {
        msisdn: normalizedNumber,
        platform: 'WEB'
      }

      if (customerId) {
        dataToSend.otpRequestType = 'SERVICE_CONNECT'
        dataToSend.customerId = customerId
        requestURL = 'user/sendotpcode'
      }

      this.$apiRequest.post(requestURL, dataToSend)
        .then(() => {
          this.otpCodeSent = true
          this.toast('success', {
            message: this.$t('toastMessages.otpCodeSent')
          })
        })
        .catch(e => {
          if (e.response.data.errors && e.response.data.errors.length) {
            this.$refs.registrationObs.setErrors({
              phoneNumber: getApiResponse('sendOtpCode', e.response.data.errors[0].type)
            })
          } else {
            this.toast('error', {
              message: this.$t('errors.serverError')
            })
          }
        })
        .finally(() => {
          this.buttonDisabled = false
        })

      if (this.otpCodeSent) {
        addDataLayerEvent('posalji_ponovo')
      }
    },
    async submitMobileRegistrationForm () {
      this.mobileRegistrationSubmitted = true
      this.dataLoading = true
      const valid = await this.$refs.registrationObs.validate()
      if (valid) {
        const normalizedNumber = this.inputNumber.replace(/[^0-9]/g, '')
        let requestURL = 'register/verifyotpcode'

        const dataToSend = {
          msisdn: normalizedNumber,
          otp: this.otpCode
        }

        if (this.$props.isConnectServiceForm) {
          dataToSend.otpRequestType = 'SERVICE_CONNECT'
          dataToSend.customerId = this.reconnectionData.customerId
          requestURL = 'user/verifyotpcode'
        }

        this.$apiRequest.post(requestURL, dataToSend)
          .then(response => {
            if (!this.$props.isConnectServiceForm && response.data.token) {
              this.checkCredentials(response.data.token, 'mob')
            } else {
              this.emitNumberForReconnection(normalizedNumber, response.data.code, response.data.customerId)
            }
          })
          .catch(e => {
            if (e.response.data.errors && e.response.data.errors.length) {
              const errorCodeType = e.response.data.errors[0].type

              if (errorCodeType === 'UnknownIdentifierError') {
                this.toast('error', {
                  message: this.$t('errors.serverError')
                })
              } else {
                this.$refs.registrationObs.setErrors({
                  otpCode: getApiResponse('verifyOtpCode', errorCodeType)
                })
              }

              if (errorCodeType === 'MtmOtpCodeExpiredError') {
                addDataLayerEvent('istekao_kod')
              }
            } else {
              this.toast('error', {
                message: this.$t('errors.serverError')
              })
            }
          })
          .finally(() => {
            this.dataLoading = false
            this.buttonDisabled = false
          })
      } else {
        this.dataLoading = false
        this.buttonDisabled = false
      }
    },
    async submitFixRegistrationForm () {
      this.landlineRegistrationSubmitted = true
      this.dataLoading = true
      const valid = await this.$refs.registrationObsFixed.validate()
      if (valid) {
        if (!this.$props.isConnectServiceForm) {
          this.sendCableRegistrationRequest()
        } else {
          this.emitNumberForCableReconnection()
        }
      } else {
        this.dataLoading = false
        this.buttonDisabled = false
      }
    },
    sendCableRegistrationRequest () {
      this.$apiRequest.post('register/verifycustomer', { customerId: this.customerId, invoiceNumber: this.billNumber })
        .then(response => {
          if (response.data.token) {
            this.checkCredentials(response.data.token, 'fix')
          }
        })
        .catch(e => {
          if (e.response.data.errors && e.response.data.errors.length) {
            const errorCodeType = e.response.data.errors[0].type
            this.toast('error', {
              message: getApiResponse('verifyCustomer', errorCodeType)
            })
            if (errorCodeType === 'MtmCustomerNotFoundError') {
              addDataLayerEvent('error_broj_racuna')
            }
          } else {
            this.toast('error', {
              message: this.$t('errors.serverError')
            })
          }
        })
        .finally(() => {
          this.dataLoading = false
          this.buttonDisabled = false
        })
    },
    handleTooltipClick (name) {
      if (name === 'bill') {
        addDataLayerEvent('info_broj_racuna')
      } else if (name === 'customerId') {
        addDataLayerEvent('info_sifra_korisnika')
      }
    },
    checkCredentials (token, registrationType) {
      const qs = encodeURIComponent(token)
      this.$apiRequest.get(`register/checkcredentials?token=${qs}`)
        .then(response => {
          // pack and save data in LS for later
          const lsData = response.data
          lsData.token = token
          lsData.type = registrationType
          lsData.phoneNumber = this.inputNumber
          lsSave('sc_registration', lsData)

          if (response.data.registered) {
            customRouter.goTo({ name: 'emailVerification' })
          } else {
            customRouter.goTo({ name: 'createAccount' })
          }
        })
        .catch(e => {
          this.toast('error', {
            message: this.$t('errors.serverError')
          })
        })
        .finally(() => {
          this.buttonDisabled = false
          this.dataLoading = false
        })
    },
    emitCancelEvent () {
      this.$emit('cancel-add')
    },
    emitNumberForReconnection (number, otpCode, customerId) {
      this.$emit('reconnection-mobile', { phoneNumber: number, otpCode: otpCode, type: 'mobile', ...this.reconnectionData, userId: customerId })
    },
    emitNumberForCableReconnection () {
      this.$emit('reconnection-cable', { customerNumber: this.customerId, invoiceNumber: this.billNumber, type: 'cable' })
      this.buttonDisabled = false
      this.dataLoading = false
    }
  },
  mounted () {
    this.$nextTick(() => {
      const hash = window.location.hash
      if (hash === '#kablovska' && !this.$props.isConnectServiceForm) {
        this.activeTab = 2
      }

      lsRemove('sc_registration')
    })
  }
}
</script>

<style scoped lang="scss" src="./registration-comp-styles/registration-form.scss"></style>
